<script setup lang="ts">
import { Remarkable } from "remarkable"
import { ArticleProps } from "./Article.props"
import { ProductAlgolia } from "~/utils/mapProducts"
import { WithContext, NewsArticle } from "schema-dts"
import { SeoBaseProps } from "~/components/seo/Base.props"
import { parse } from "node-html-parser"
import { checkEnvSite } from "~~/server/utils/getAlgoliaConfig"
import { productUtils } from "~/utils/product"
import { useI18n } from "vue-i18n"

const { t: $t } = useI18n()

const baseUrl = useRuntimeConfig().public.BASE_URL_APP
const route = useRoute()
const searchQuery = ref((route.query.query as string) || "")

const indexProducts = useAlgoliaInitIndex(`TOP_PRODUCTS${checkEnvSite()}`)
const remarkable = new Remarkable({ html: true })
const props = defineProps<ArticleProps>()

const root = parse(remarkable.render(props.content))
const mdText = computed<string>(() => root?.toString() ?? "")

const findItemsFromAlgolia = async (productCodes: string[]) => {
  const response = await indexProducts.getObjects(
    productCodes.map((code) => `product_${code}`)
  )

  const products = response?.results ?? []

  const itemsInStock =
    await productUtils.getItemsInStockOrRelatedItemsFromAlgolia(products)

  return mapProductsAlgolia(itemsInStock as ProductAlgolia[]).filter(
    (item) => item as ProductAlgolia
  )
}

const { data: productBlocksSuggestion } = await useAsyncData(
  "findItemsFromAlgolia",
  () =>
    findItemsFromAlgolia(
      props.suggestionProducts.products.map((item) => `${item.productCode}`)
    ),
  {
    immediate: true,
    server: true,
    lazy: false
  }
)

const seoRef = computed((): SeoBaseProps => {
  return {
    ...props.seo,
    metaTitle: props.seo?.metaTitle || props.title
  }
})

const wordCounter = props.content
  ? useWordCounter(props.content, { isHtml: true })
  : {
      wordsCount: 0
    }

const jsonLd: ComputedRef<WithContext<NewsArticle>> = computed(() => ({
  "@context": "https://schema.org",
  "@type": "NewsArticle",
  headline: props.title,
  image: [props.cover.image.image.url],
  datePublished: props.firstPublishingDate,
  dateModified: props.lastPublishingDate,
  ...(props.author
    ? {
        author: {
          "@type": "Person",
          name: props.author ?? ""
        }
      }
    : {}),
  publisher: {
    "@type": "Organization",
    url: "https://www.topfarmacia.it/",
    name: "Top Farmacia",
    logo: {
      "@type": "ImageObject",
      url: `${baseUrl}/images/LogoTopFarmaciaOnline.svg`
    }
  },
  articleSection:
    props.breadcrumbsLinks.links[props.breadcrumbsLinks.links.length - 1].label,
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": route.fullPath
  },
  description: props.description ?? "",
  wordCount: wordCounter.wordsCount
}))

useHead({
  script: [
    {
      type: "application/ld+json",
      children: JSON.stringify(cleanJsonLdObject(jsonLd.value))
    }
  ]
})
</script>

<template>
  <div class="article-wrapper grid-standard relative items-start">
    <SeoBase v-if="seo" v-bind="seoRef" />
    <div class="negative-margined col-span-full row-start-1">
      <UtilsBreadcrumbs v-bind="breadcrumbsLinks" />
    </div>
    <div class="negative-margined col-span-full row-start-1 mt-8 md:mx-0">
      <div class="mx-auto bg-default-green-10 px-4">
        <!-- Search form -->
        <div class="col-span-full">
          <ClientOnly>
            <SearchMagazineSearchBar
              v-model="searchQuery"
              :placeholder="$t('magazine.search.placeholder')"
            />
            <template #fallback>
              <SkeletonMagazineSearchBar />
            </template>
          </ClientOnly>
        </div>
      </div>
    </div>

    <ProductBlocksBrandsCategoryCover
      v-bind="cover"
      class="negative-margined col-span-full row-start-3 md:mx-0"
    />

    <h1
      v-if="title"
      class="
        elephant-bold
        md:mammoth-bold
        col-span-full
        row-start-4
        my-6
        md:col-span-10 md:col-start-2
      "
    >
      {{ title }}
    </h1>

    <div
      id="textWrapper"
      class="
        wiki-article-text
        col-span-full
        row-start-5
        mb-6
        flex flex-col
        md:col-start-2
        md:mb-10
      "
      :class="
        suggestionProducts.products.length ? 'md:col-span-5' : 'md:col-span-10'
      "
    >
      <div class="col-span-full" v-if="contents && contents.length > 0">
        <DynamicContent
          class="cms-content"
          style="overflow: hidden"
          :components="contents"
        />
      </div>
      <div id="textWrapper" v-html="mdText" v-else></div>
    </div>

    <div
      class="
        col-span-full
        row-start-6
        flex flex-col
        gap-4
        md:col-span-10 md:col-start-2
        md:mb-8
        md:flex-row
        md:items-center
        md:justify-between
      "
    >
      <div class="flex flex-wrap items-center gap-4">
        <span class="mouse -mr-2" v-if="tags.length">
          {{ $t("article.categories") }}:
        </span>

        <BadgeTag v-for="tag in tags" v-bind="tag" />
      </div>

      <UtilsShare class="mb-2">
        <template #default>
          <UtilsButton
            :text="$t('article.share')"
            class="custom-btn-no-padding"
            theme="link-green"
          >
            <template #icon>
              <UtilsIcon name="Share.svg" color="green-main" class="h-5 w-5" />
            </template>
          </UtilsButton>
        </template>
      </UtilsShare>
    </div>
    <ProductBlocksSuggestion
      v-if="suggestionProducts.products.length"
      :products="productBlocksSuggestion"
      class="
        negative-margined
        col-span-full
        row-start-7
        md:col-span-4 md:col-start-8
        md:row-start-5
        md:m-0
      "
    />
    <!-- TODO: Aggiunta la sezione  -->
    <!-- <div v-if="suggestionCategories" class="col-span-full pb-8 pt-6">
      <p class="pig-bold md:hyppo-bold mb-4">
        {{ $t("article.moreUsedSubject") }}
      </p>

      <div class="flex gap-2">
        <UtilsButton
          v-for="(item, index) in suggestionCategories"
          :key="index"
          class="drop-shadow-01"
          theme="contained-white"
          v-bind="item"
        />
      </div>
    </div> -->

    <BannerNewsletter class="col-span-full row-start-[8]" />
    <div
      class="
        padded
        negative-margined
        bg-grey-main
        col-span-full
        row-start-[9]
        py-6
      "
      v-if="suggestionPosts.length"
    >
      <h3 class="hyppo-bold md:elephant-bold mb-2 md:mb-6">
        {{ $t("article.suggestion") }}
      </h3>

      <div class="flex flex-col justify-center gap-6 md:flex-row">
        <!-- For some reason, the CMS returns 'imageData' instead of image -->
        <CardBlogPost
          v-if="suggestionPosts"
          v-for="(post, index) in suggestionPosts"
          :key="index"
          :image="post.imageData"
          :title="post.title"
          :path="post.path"
          :tags="post.tags"
          :text="post.text"
          class="flex-1"
        />
      </div>
    </div>
    <CartUtilsLogin
      :upperText="$t('loginBanner.type2.upperText')"
      show-login-button
      class="negative-margined col-span-full row-start-[10]"
    />
  </div>
</template>
